import React from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../ui/atoms';

const INPUT_CLASSES = "form-input-inset";

const WidgetAppearance = ({ widgetConfig, onConfigChange, onSave, saving }) => {
  const { t } = useTranslation();
  const [showCustomColor, setShowCustomColor] = React.useState(false);
  const [customColorValue, setCustomColorValue] = React.useState('');
  const [isEditing, setIsEditing] = React.useState(false);

  // Check if current color is one of the preset colors
  const presetColors = ['#3b82f6', '#8b5cf6', '#f97316', '#06b6d4', '#000000'];
  const isPresetColor = presetColors.includes(widgetConfig.widget_color);

  React.useEffect(() => {
    // If color is not a preset, show custom input
    if (widgetConfig.widget_color && !isPresetColor) {
      setShowCustomColor(true);
      setCustomColorValue(widgetConfig.widget_color);
    } else {
      setShowCustomColor(false);
    }
  }, [widgetConfig.widget_color, isPresetColor]);

  const handleSave = async () => {
    await onSave();
    setIsEditing(false);
  };

  if (widgetConfig.widget_type === 'API') {
    return null;
  }

  return (
    <div 
      className={`group border border-gray-200 rounded-lg bg-white transition-colors ${
        !isEditing ? 'hover:bg-gray-50 cursor-pointer' : ''
      }`}
      onClick={!isEditing ? () => setIsEditing(true) : undefined}
    >
      <div className="p-6 pb-4 relative">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{t('widgets.config.appearance.title')}</h3>
          {!isEditing && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <PencilIcon className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </div>
        
        {!isEditing ? (
          /* View Mode */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">
                {t('widgets.config.appearance.primaryColor')}
              </label>
              <div className="flex items-center space-x-2 mt-1">
                <div 
                  className="w-10 h-10 rounded-full border-2 border-gray-300"
                  style={{ backgroundColor: widgetConfig.widget_color || '#3b82f6' }}
                ></div>
                <span className="text-sm text-gray-900">
                  {widgetConfig.widget_color || '#3b82f6'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">
                {t('widgets.config.appearance.widgetPosition')}
              </label>
              <p className="text-sm text-gray-900 mt-1 capitalize">
                {widgetConfig.widget_position || 'right'}
              </p>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
          <div>
            <label htmlFor="widget_color" className="block text-sm font-medium text-gray-700 mb-2">
              {t('widgets.config.appearance.primaryColor')}
            </label>
            {/* Color Swatches */}
            <div className="flex items-center space-x-2 mb-3 flex-wrap gap-2">
              {['#3b82f6', '#8b5cf6', '#f97316', '#06b6d4', '#000000'].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    setShowCustomColor(false);
                    onConfigChange({ target: { name: 'widget_color', value: color } });
                  }}
                  className={`w-10 h-10 rounded-full border-2 ${
                    widgetConfig.widget_color === color && !showCustomColor
                      ? 'border-primary-600 ring-2 ring-primary-200'
                      : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                >
                  {widgetConfig.widget_color === color && !showCustomColor && (
                    <svg className="w-5 h-5 text-white mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setShowCustomColor(true);
                  setCustomColorValue(widgetConfig.widget_color || '#3b82f6');
                }}
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-xs font-medium ${
                  showCustomColor
                    ? 'border-primary-600 ring-2 ring-primary-200 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-500 hover:border-gray-400'
                }`}
                title="Custom color"
              >
                C
              </button>
            </div>
            {/* Custom Color Input - only show when CUSTOM is selected */}
            {showCustomColor && (
              <div className="flex items-center space-x-2 mb-3">
                <input
                  id="widget_color"
                  name="widget_color"
                  type="color"
                  className="h-10 w-12 p-1 rounded-md border-gray-300"
                  value={customColorValue || '#3b82f6'}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    setCustomColorValue(newColor);
                    onConfigChange({ target: { name: 'widget_color', value: newColor } });
                  }}
                />
                <input
                  id="widget_color_text"
                  name="widget_color"
                  type="text"
                  className={INPUT_CLASSES}
                  value={customColorValue || '#3b82f6'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomColorValue(value);
                    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                      onConfigChange({ target: { name: 'widget_color', value: value } });
                    }
                  }}
                  placeholder="#3b82f6"
                  maxLength={7}
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            )}
            <p className="mt-2 text-sm text-gray-500">
              {t('widgets.config.appearance.primaryColorDesc')}
            </p>
          </div>

          <div>
            <label htmlFor="widget_position" className="block text-sm font-medium text-gray-700 mb-2">
              {t('widgets.config.appearance.widgetPosition')}
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => onConfigChange({ target: { name: 'widget_position', value: 'left' } })}
                className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                  widgetConfig.widget_position === 'left'
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <span className="text-sm font-medium">Left</span>
                  {/* Laptop illustration */}
                  <div className="w-16 h-12 border-2 border-gray-400 rounded relative bg-gray-50">
                    {/* Screen */}
                    <div className="absolute inset-1 border border-gray-300 rounded bg-white"></div>
                    {/* Widget indicator on left */}
                    <div className={`absolute left-0 bottom-1 w-4 h-4 rounded ${
                      widgetConfig.widget_position === 'left' ? 'bg-primary-600' : 'bg-gray-300'
                    }`}></div>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => onConfigChange({ target: { name: 'widget_position', value: 'right' } })}
                className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                  widgetConfig.widget_position === 'right' || !widgetConfig.widget_position
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <span className="text-sm font-medium">Right</span>
                  {/* Laptop illustration */}
                  <div className="w-16 h-12 border-2 border-gray-400 rounded relative bg-gray-50">
                    {/* Screen */}
                    <div className="absolute inset-1 border border-gray-300 rounded bg-white"></div>
                    {/* Widget indicator on right */}
                    <div className={`absolute right-0 bottom-1 w-4 h-4 rounded ${
                      (widgetConfig.widget_position === 'right' || !widgetConfig.widget_position) ? 'bg-primary-600' : 'bg-gray-300'
                    }`}></div>
                  </div>
                </div>
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              {t('widgets.config.appearance.widgetPositionDesc')}
            </p>
          </div>
          </div>
        )}
      </div>
      {isEditing && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
          <Button type="button" variant="secondary" size="md" shape="square" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="md" shape="square" onClick={handleSave} disabled={saving} loading={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WidgetAppearance;

